
import React, { createContext, useState, useCallback, useContext } from 'react';

interface ListingFormContextType {
  formData: {
    title: string;
    description: string;
    price: string;
    isPriceNegotiable: string;
    category: string;
    subcategory: string;
    condition: string;
    country: string;
    location: string;
    deliveryOption: string;
    shippingCost: string;
    stock: string;
    phone: string;
    phoneCountryCode: string;
    whatsappEnabled: boolean;
    viberEnabled: boolean;
    email: string;
    [key: string]: any;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  errors: {
    [key: string]: string;
  };
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  validateForm: () => boolean;
  handleInputChange: (name: string, value: any) => void;
  images: File[];
  setImages: React.Dispatch<React.SetStateAction<File[]>>;
  stockItems: { id: string; quantity: number }[];
  showStockWarning: boolean;
  setShowStockWarning: React.Dispatch<React.SetStateAction<boolean>>;
  isAuthenticated: boolean;
}

export interface ListingFormProviderProps {
  children: React.ReactNode;
  initialData?: any;
  isAuthenticated?: boolean;
}

const ListingFormContext = createContext<ListingFormContextType | undefined>(undefined);

export const ListingFormProvider: React.FC<ListingFormProviderProps> = ({ 
  children, 
  initialData = {}, 
  isAuthenticated = true
}) => {
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    description: initialData.description || '',
    price: initialData.price || '',
    isPriceNegotiable: initialData.isPriceNegotiable || 'false',
    category: initialData.category_id || '',
    subcategory: initialData.subcategory_id || '',
    condition: initialData.condition || '',
    country: initialData.country || 'albania',
    location: initialData.location || '',
    deliveryOption: initialData.delivery_option || 'pickup_only',
    shippingCost: initialData.shipping_cost || '',
    stock: initialData.stock || '1',
    phone: initialData.phone || '',
    phoneCountryCode: initialData.phone_country_code || '+355',
    whatsappEnabled: initialData.whatsapp_enabled || false,
    viberEnabled: initialData.viber_enabled || false,
    email: initialData.contact_email || '',
    ...initialData,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [images, setImages] = useState<File[]>([]);
  const [stockItems, setStockItems] = useState<{ id: string; quantity: number }[]>([]);
  const [showStockWarning, setShowStockWarning] = useState(false);

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.title) newErrors.title = 'Titulli është i nevojshëm';
    if (!formData.description) newErrors.description = 'Përshkrimi është i nevojshëm';
    if (!formData.price) newErrors.price = 'Çmimi është i nevojshëm';
    if (!formData.category) newErrors.category = 'Kategoria është e nevojshme';
    if (!formData.subcategory) newErrors.subcategory = 'Nënkategoria është e nevojshme';
    if (!formData.condition) newErrors.condition = 'Gjendja është e nevojshme';
    if (!formData.country) newErrors.country = 'Shteti është i nevojshëm';
    if (!formData.location) newErrors.location = 'Qyteti është i nevojshëm';
    if (!formData.deliveryOption) newErrors.deliveryOption = 'Opsioni i transportit është i nevojshëm';
    if (!formData.stock) newErrors.stock = 'Sasia është e nevojshme';
    if (!formData.phone) newErrors.phone = 'Numri i telefonit është i nevojshëm';
    if (!isAuthenticated && !formData.email) newErrors.email = 'Email-i është i nevojshëm për përdoruesit pa llogari';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isAuthenticated]);

  const handleInputChange = useCallback((name: string, value: any) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
    setErrors(prevErrors => {
      const { [name]: removedError, ...rest } = prevErrors;
      return rest;
    });
  }, []);

  const contextValue = {
    formData,
    setFormData,
    errors,
    setErrors,
    validateForm,
    handleInputChange,
    images,
    setImages,
    stockItems,
    showStockWarning,
    setShowStockWarning,
    isAuthenticated,
  };

  return (
    <ListingFormContext.Provider value={contextValue}>
      {children}
    </ListingFormContext.Provider>
  );
};

export const useListingForm = () => {
  const context = useContext(ListingFormContext);
  if (!context) {
    throw new Error('useListingForm must be used within a ListingFormProvider');
  }
  return context;
};
